import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, MapPin, Package } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { token, user, loading: authLoading } = useAuth();
  const { refreshCart } = useCart();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
  });

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) return;
    
    if (!user || !token) {
      toast.error("Please login to checkout");
      navigate("/");
      return;
    }
    fetchCart();
  }, [user, token, authLoading]);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCartItems(response.data.items || []);
      if (response.data.items?.length === 0) {
        toast.error("Your cart is empty");
        navigate("/cart");
      }
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.product.offer_price || item.product.price;
      return total + price * item.quantity;
    }, 0);
  };

  const handleInputChange = (e) => {
    setShippingAddress({
      ...shippingAddress,
      [e.target.name]: e.target.value,
    });
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Create order
      const orderResponse = await axios.post(
        `${API}/orders/create`,
        { shipping_address: shippingAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { order_id, razorpay_order_id, amount, currency, razorpay_key_id } = orderResponse.data;

      if (!razorpay_key_id) {
        toast.error("Payment gateway not configured. Please contact support.");
        setProcessing(false);
        return;
      }

      // Load Razorpay script
      const res = await loadRazorpayScript();
      if (!res) {
        toast.error("Failed to load payment gateway");
        setProcessing(false);
        return;
      }

      // Initialize Razorpay
      const options = {
        key: razorpay_key_id,
        amount: amount,
        currency: currency,
        name: "OrgShop",
        description: "Order Payment",
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(
              `${API}/orders/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Payment successful! Order placed.");
            // Cart is cleared server-side only after payment is verified — sync the badge.
            refreshCart();
            navigate("/dashboard");
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: shippingAddress.name,
          contact: shippingAddress.phone,
        },
        theme: {
          color: "#2A4037",
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Checkout failed:", error);
      toast.error("Failed to create order");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="checkout-loading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <Navbar />

      <div className="min-h-screen py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-8" data-testid="checkout-page-title">
            Checkout
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Shipping Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleCheckout} className="bg-card rounded-2xl p-6 shadow-soft" data-testid="checkout-form">
                <div className="flex items-center space-x-3 mb-6">
                  <MapPin className="w-6 h-6 text-primary" />
                  <h2 className="font-heading text-2xl font-bold">Shipping Address</h2>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Full Name</label>
                      <Input
                        name="name"
                        value={shippingAddress.name}
                        onChange={handleInputChange}
                        required
                        data-testid="checkout-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone</label>
                      <Input
                        name="phone"
                        type="tel"
                        value={shippingAddress.phone}
                        onChange={handleInputChange}
                        required
                        data-testid="checkout-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Address Line 1</label>
                    <Input
                      name="address_line1"
                      value={shippingAddress.address_line1}
                      onChange={handleInputChange}
                      required
                      data-testid="checkout-address1-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Address Line 2 (Optional)</label>
                    <Input
                      name="address_line2"
                      value={shippingAddress.address_line2}
                      onChange={handleInputChange}
                      data-testid="checkout-address2-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">City</label>
                      <Input
                        name="city"
                        value={shippingAddress.city}
                        onChange={handleInputChange}
                        required
                        data-testid="checkout-city-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">State</label>
                      <Input
                        name="state"
                        value={shippingAddress.state}
                        onChange={handleInputChange}
                        required
                        data-testid="checkout-state-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Pincode</label>
                      <Input
                        name="pincode"
                        value={shippingAddress.pincode}
                        onChange={handleInputChange}
                        required
                        data-testid="checkout-pincode-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <Button
                    type="submit"
                    disabled={processing}
                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full py-6 text-lg font-medium"
                    data-testid="checkout-pay-btn"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    {processing ? "Processing..." : `Pay ₹${calculateTotal().toFixed(2)}`}
                  </Button>
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-2xl p-6 shadow-soft sticky top-24" data-testid="checkout-summary">
                <div className="flex items-center space-x-3 mb-6">
                  <Package className="w-6 h-6 text-primary" />
                  <h2 className="font-heading text-2xl font-bold">Order Summary</h2>
                </div>

                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.product.id} className="flex space-x-3" data-testid="checkout-item">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        <img
                          src={item.product.images?.[0] || "https://images.unsplash.com/photo-1564682628523-316b685c5d7b?w=100"}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-1">{item.product.name}</h3>
                        <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                        <p className="text-sm font-bold text-primary">₹{((item.product.offer_price || item.product.price) * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-semibold">₹{calculateTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between mb-4">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="font-semibold">Free</span>
                  </div>
                  <div className="flex justify-between pt-4 border-t border-border">
                    <span className="font-heading text-lg font-semibold">Total</span>
                    <span className="font-heading text-2xl font-bold text-primary" data-testid="checkout-total">
                      ₹{calculateTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
